import React from "react";
import { Link } from "react-router-dom";
import { Compass, GraduationCap, Briefcase, Users, MessageCircle, Calendar, Sparkles, BookOpen, Award, Building2 } from "lucide-react";
import { PageHero, Section, Card } from "@/components/Section";
import { IMAGES } from "@/lib/site";

const personnalise = [
  "Mieux se connaître",
  "Découvrir les formations",
  "Construire un projet professionnel cohérent",
  "Préparer son insertion dans le monde du travail ou la poursuite d'études",
];

const decouvrirFormations = [
  { t: "Visites de lycées professionnels", d: "Pour explorer la diversité des filières et confirmer son choix." },
  { t: "Découverte de l'enseignement supérieur", d: "Rencontres avec des écoles, BTS et universités du territoire." },
  { t: "Visite du CEFOC", d: "Découvrir l'apprentissage et la formation continue dans nos métiers." },
  { t: "Compagnons du Devoir", d: "À la rencontre d'une voie d'excellence par l'apprentissage du métier." },
];

const monde = [
  { icon: Building2, t: "Visites d'entreprises", d: "Immersions ciblées dans des entreprises du bassin angevin." },
  { icon: Briefcase, t: "Rencontres avec des agences d'intérim", d: "Comprendre le marché du travail et les opportunités locales." },
  { icon: Sparkles, t: "Industries et entreprises locales", d: "Découverte de secteurs en tension et de métiers porteurs." },
  { icon: Users, t: "Mission Locale", d: "Échanges pour préparer l'après-bac et l'insertion professionnelle." },
  { icon: Award, t: "Métiers de l'armée", d: "Présentations des parcours civils et militaires possibles." },
  { icon: MessageCircle, t: "Témoignages de professionnels", d: "Des intervenants partagent leur expérience et leur quotidien." },
];

const ateliers = [
  "Rencontres avec différents intervenants professionnels",
  "Interventions de l'association C'Possible",
  "Découverte du monde de l'entreprise",
  "Acquisition des codes professionnels",
  "Travail sur le savoir-être et les compétences comportementales",
];

const familles = [
  { icon: Calendar, t: "Réunions d'information", d: "Des temps réguliers pour comprendre les étapes et les choix à venir." },
  { icon: MessageCircle, t: "Échanges avec les équipes", d: "Disponibilité des enseignants et de la vie scolaire à chaque grande étape." },
  { icon: Compass, t: "Accompagnement aux choix", d: "Un suivi individualisé pour aider chaque famille dans les décisions clés." },
];

const evenements = [
  "Participation aux forums d'orientation",
  "Salons étudiants",
  "Rencontres avec les établissements de formation",
];

export default function Orientation() {
  return (
    <div data-testid="orientation-page">
      <PageHero
        eyebrow="Accompagnement & orientation"
        title="Construire un parcours de réussite, étape par étape"
        subtitle="Au lycée, l'accompagnement à l'orientation est un élément essentiel de la réussite des élèves. Tout au long de leur parcours, ils bénéficient d'un suivi personnalisé pour construire progressivement leur projet d'avenir."
        image={IMAGES.abstract}
      />

      <Section eyebrow="Un accompagnement personnalisé" title="Intégré à l'emploi du temps de chaque élève">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-start">
          <div className="space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>
              Le soutien au parcours et l'accompagnement personnalisé sont inscrits à l'emploi du
              temps. Ces séances permettent à chaque élève d'avancer à son rythme et de prendre du
              recul sur son projet.
            </p>
            <p>L'objectif : permettre à chacun de…</p>
            <ul className="grid gap-3">
              {personnalise.map((p) => (
                <li key={p} className="flex items-start gap-3 rounded-2xl border border-black/5 bg-brand-cream p-4">
                  <Compass className="mt-0.5 h-5 w-5 shrink-0 text-brand-slate-dark" />
                  <span className="text-sm font-medium text-brand-slate-dark">{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="overflow-hidden rounded-3xl">
            <img src={IMAGES.values} alt="Accompagnement personnalisé" className="h-full w-full object-cover" />
          </div>
        </div>
      </Section>

      <Section
        wrapper="bg-brand-sand-wrapper/40"
        eyebrow="Découvrir les formations"
        title="Sortir des murs du lycée pour s'inspirer"
      >
        <div className="grid gap-6 sm:grid-cols-2">
          {decouvrirFormations.map((d, i) => (
            <Card key={d.t} data-testid={`orientation-formation-${i}`}>
              <GraduationCap className="h-7 w-7 text-brand-sage-dark" />
              <h3 className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">{d.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{d.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section eyebrow="Découvrir le monde professionnel" title="Aller à la rencontre des métiers et des entreprises">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {monde.map((m, i) => (
            <Card key={m.t} data-testid={`orientation-monde-${i}`}>
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-slate/10 text-brand-slate-dark">
                <m.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-heading text-base font-semibold text-brand-slate-dark">{m.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{m.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section wrapper="bg-brand-slate-dark text-brand-cream">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-sand">
              Interventions & ateliers
            </p>
            <h2 className="mt-4 font-heading text-3xl font-bold sm:text-4xl">
              Préparer concrètement l'entrée dans le monde du travail
            </h2>
            <ul className="mt-6 space-y-3">
              {ateliers.map((a) => (
                <li key={a} className="flex items-start gap-3 text-sm">
                  <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-brand-sand" />
                  <span className="text-brand-cream/90">{a}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="overflow-hidden rounded-3xl">
            <img src={IMAGES.classroom} alt="Ateliers professionnels" className="h-full w-full object-cover" />
          </div>
        </div>
      </Section>

      <Section eyebrow="Informer & accompagner les familles" title="Vous êtes pleinement associés au parcours">
        <div className="grid gap-6 md:grid-cols-3">
          {familles.map((f, i) => (
            <Card key={f.t} data-testid={`orientation-famille-${i}`}>
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-sand/30 text-brand-slate-dark">
                <f.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">{f.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section
        wrapper="bg-brand-sand-wrapper/40"
        eyebrow="Témoignages & parcours"
        title="S'inspirer des trajectoires de nos anciens élèves"
      >
        <div className="grid gap-6 md:grid-cols-3">
          <Card data-testid="orientation-temoignage-0">
            <Users className="h-7 w-7 text-brand-sage-dark" />
            <h3 className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">
              Rencontres d'anciens élèves
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Des temps d'échange réguliers entre promotions, pour transmettre les réalités du terrain.
            </p>
          </Card>
          <Card data-testid="orientation-temoignage-1">
            <GraduationCap className="h-7 w-7 text-brand-sage-dark" />
            <h3 className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">
              Étudiants en BTS
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Des témoignages d'anciens lycéens poursuivant aujourd'hui leurs études en BTS.
            </p>
          </Card>
          <Card data-testid="orientation-temoignage-2">
            <Building2 className="h-7 w-7 text-brand-sage-dark" />
            <h3 className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">
              Parcours à l'Esthua d'Angers
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Présentations de parcours d'études à l'Esthua d'Angers et dans d'autres établissements
              du supérieur.
            </p>
          </Card>
        </div>
      </Section>

      <Section eyebrow="Évènements d'orientation" title="Aller à la rencontre des formations">
        <div className="grid gap-6 sm:grid-cols-3">
          {evenements.map((e, i) => (
            <Card key={e} data-testid={`orientation-evenement-${i}`}>
              <Calendar className="h-7 w-7 text-brand-slate" />
              <h3 className="mt-4 font-heading text-base font-semibold text-brand-slate-dark">{e}</h3>
            </Card>
          ))}
        </div>
      </Section>

      <Section wrapper="bg-brand-slate text-brand-cream">
        <div className="grid items-center gap-8 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <h2 className="font-heading text-3xl font-bold sm:text-4xl">
              Chaque élève accompagné vers son propre chemin de réussite
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-brand-cream/85">
              Au lycée Joseph Wresinski, nous croyons qu'aucune ambition n'est inaccessible quand elle
              est nourrie par un accompagnement humain et exigeant. Chaque élève est invité à
              construire son projet, à découvrir ses talents et à préparer sereinement son avenir.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link
              to="/contact"
              data-testid="orientation-cta-contact"
              className="inline-flex items-center gap-2 rounded-full bg-brand-sand-dark px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-sand"
            >
              Échanger avec un référent
            </Link>
            <Link
              to="/erasmus"
              data-testid="orientation-cta-erasmus"
              className="inline-flex items-center gap-2 rounded-full border border-brand-cream/30 px-6 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:bg-brand-cream hover:text-brand-slate-dark"
            >
              Découvrir Erasmus+
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
